import { Component, OnDestroy, OnInit } from '@angular/core';
import { EnvironmentType } from '@models/environment.model';
import { environment } from '../../../../environments/environment';
import { UserRoles } from '@authentication/user-roles';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NotificationsModalComponent } from '@common/components/notifications-modal/notifications-modal.component';
import { AuthService } from '@services/auth.service';
import { OperatorService } from '@services/operator.service';
import { ActivatedRoute, ActivationEnd, NavigationEnd, Router } from '@angular/router';
import { AdminService } from '@services/admin.service';
import { map, take } from 'rxjs/operators';
import { filter, Observable, shareReplay, Subscription } from 'rxjs';
import { Garages } from '@models/garages';
import { GarageService } from '@services/garage.service';
import { NotificationService } from '@services/notification.service';
import { safeSessionStorage } from '../../safe-storage';

interface NavLink {
  label: string;
  address?: string;
  isExternal: boolean;
  activePaths?: string[];
  subMenu?: NavLink[];
}

@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
})
export class NavBarComponent implements OnInit, OnDestroy {
  protected readonly EnvironmentType = EnvironmentType;
  protected readonly environment = environment;
  protected readonly UserRoles = UserRoles;

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private operatorService: OperatorService,
    private router: Router,
    private route: ActivatedRoute,
    private adminService: AdminService,
    private garageService: GarageService,
    private notificationService: NotificationService
  ) {
    this.route.queryParams.subscribe((params) => {
      if (params['hideMenu']) {
        safeSessionStorage.setItem('hideMenu', params['hideMenu']);
      }
      this.hideMenu = safeSessionStorage.getItem('hideMenu') === 'true';
    });

    this.router.events.subscribe((val) => {
      if (val instanceof ActivationEnd) {
        if (val.snapshot.url) {
          this.path = '/' + val.snapshot.url.join('/');
        }
        if (this.mustSetOperators) {
          this.navigateToSettings();
        }
      }
    });
  }

  userGarageId$: Observable<number>;

  collapsed = true;
  links: NavLink[] = [];
  openIndex: number | null = null;
  subRouteActiveStates: boolean[] = [];

  myUserRoles = '';
  isSingleRole = false;
  userEmail = null;
  showManual = false;
  showUEManual = false;
  showAdministrateMenu = false;
  showOperatorMenu = false;
  displayedUserName = '';
  selectedSubcontatorId = '';
  mustSetOperators = false;
  myOrganisation = '';
  hideMenu = false;
  numberOfUnreadNotifications = 0;
  path: string;
  manualPath = '';
  ueManualPath = '';
  userOperatorId: string;

  currentLanguage = '';
  currentFlag = '';

  private isLargeScreen: boolean;
  private mediaQuery: MediaQueryList;
  private mediaQueryListener: (e: MediaQueryListEvent) => void;
  private routerSub: Subscription;

  ngOnInit() {
    this.setSelectedLanguage();
    this.init();

    this.mediaQuery = window.matchMedia('(min-width: 1200px)');
    this.isLargeScreen = this.mediaQuery.matches;
    this.mediaQueryListener = (e: MediaQueryListEvent) => {
      this.isLargeScreen = e.matches;
      if (!e.matches) this.openIndex = null;
    };
    this.mediaQuery.addEventListener('change', this.mediaQueryListener);

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.updateActiveStates());
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.mediaQuery?.removeEventListener('change', this.mediaQueryListener);
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  collapseMenu(): void {
    this.collapsed = true;
  }

  closeSubMenu(): void {
    this.openIndex = null;
    this.collapseMenu();
  }

  openOnHover(index: number): void {
    if (this.isLargeScreen) this.openIndex = index;
  }

  closeOnHover(): void {
    if (this.isLargeScreen) this.openIndex = null;
  }

  toggleMenu(index: number): void {
    if (!this.isLargeScreen) {
      this.openIndex = this.openIndex === index ? null : index;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  isLinkActive(link: NavLink): boolean {
    if (!link.activePaths || !this.path) return false;
    return link.activePaths.some((p) => (p === '/' ? this.path === '/' : this.path.startsWith(p)));
  }

  openNotifications() {
    const opts: NgbModalOptions = {
      windowClass: 'notificationModal',
      backdropClass: 'notificationModalBackdrop',
    };
    this.modalService.open(NotificationsModalComponent, opts);
  }

  private _initObservables() {
    if (this.authService.userHasAnyRole([UserRoles.UETrafikledare])) {
      this.userGarageId$ = this.garageService.getGarageState().pipe(
        filter((garages: Garages) => garages.data.length > 0),
        map((garages: Garages) => garages.data[0].id),
        shareReplay(1)
      );
    }
  }

  private buildLinks(): void {
    const links: NavLink[] = [];
    const hasRole = (roles: UserRoles[]) => this.authService.userHasAnyRole(roles);

    if (
      hasRole([
        UserRoles.NobinaTrafikledare,
        UserRoles.NobinaLeveransansvarig,
        UserRoles.OperatorTrafikledare,
        UserRoles.OperatorInformator,
      ])
    ) {
      links.push({
        label: $localize`:@@app.orders:Hem`,
        address: '/order-new/start',
        isExternal: true,
        activePaths: ['/order', '/unboundorder'],
      });
    }

    if (hasRole([UserRoles.UETrafikledare])) {
      links.push({
        label: $localize`:@@app.home:Hem`,
        address: '/order-new/start',
        isExternal: true,
        activePaths: ['/subcontractor-orders'],
      });
    }

    if (
      hasRole([
        UserRoles.NobinaTrafikledare,
        UserRoles.NobinaLeveransansvarig,
        UserRoles.OperatorTrafikledare,
      ])
    ) {
      links.push({
        label: $localize`:@@app.history:Historik`,
        address: 'history',
        isExternal: false,
      });
    }

    if (hasRole([UserRoles.UETrafikledare])) {
      links.push({
        label: $localize`:@@app.history:Historik`,
        address: `subcontractor/${this.selectedSubcontatorId}/history`,
        isExternal: false,
      });
    }

    if (
      hasRole([UserRoles.NobinaLeveransansvarig, UserRoles.UETrafikledare]) &&
      environment.enableBilling
    ) {
      links.push({
        label: $localize`:@@app.follow-up:Uppföljning`,
        address: 'billing/outcomes',
        isExternal: false,
      });
    }

    if (hasRole([UserRoles.OperatorTrafikledare]) && environment.enableBilling) {
      links.push({
        label: $localize`:@@app.follow-up:Uppföljning`,
        address: 'billing/reports/create-report',
        isExternal: false,
      });
    }

    if (
      hasRole([
        UserRoles.NobinaTrafikledare,
        UserRoles.NobinaLeveransansvarig,
        UserRoles.OperatorTrafikledare,
        UserRoles.OperatorInformator,
        UserRoles.UETrafikledare,
      ])
    ) {
      const planningSubMenu: NavLink[] = [];
      if (
        hasRole([
          UserRoles.NobinaTrafikledare,
          UserRoles.NobinaLeveransansvarig,
          UserRoles.OperatorTrafikledare,
          UserRoles.UETrafikledare,
        ])
      ) {
        planningSubMenu.push({
          label: $localize`:@@app.drive-orders:Körorder`,
          address: 'order-new/planning',
          isExternal: true,
        });
      }
      planningSubMenu.push({
        label: $localize`:@@app.calendar:Kalender`,
        address: 'order-new/calendar',
        isExternal: true,
      });
      if (
        hasRole([
          UserRoles.NobinaTrafikledare,
          UserRoles.NobinaLeveransansvarig,
          UserRoles.OperatorInformator,
          UserRoles.OperatorTrafikledare,
        ])
      ) {
        planningSubMenu.push({
          label: $localize`:@@app.menu.route:Linjesträckningar`,
          address: 'route/editor',
          isExternal: true,
        });
      }
      links.push({
        label: $localize`:@@app.planning:Planering`,
        isExternal: false,
        subMenu: planningSubMenu,
      });
    }

    if (hasRole([UserRoles.NobinaLeveransansvarig])) {
      const administrationSubMenu: NavLink[] = [
        {
          label: $localize`:@@admin.menu.import-order:Läs in planerad beställning`,
          address: 'settings/import-order',
          isExternal: false,
        },
        {
          label: $localize`:@@admin.menu.import-timetable:Hantera linjer och linjevarianter`,
          address: 'settings/import-timetable',
          isExternal: false,
        },
        {
          label: $localize`:@@admin.menu.subcontractors:Hantera utförare och trafikoperatörer`,
          address: '/garage/subcontractors',
          isExternal: true,
        },
      ];
      if (environment.enableDeviationPage) {
        administrationSubMenu.push({
          label: $localize`:@@admin.menu.deviation:Trafikjournal`,
          address: 'order-new/deviation',
          isExternal: true,
        });
      }
      administrationSubMenu.push(
        {
          label: $localize`:@@admin.menu.connect-routevariant:Koppla linjevarianter`,
          address: 'settings/connect-routevariant',
          isExternal: false,
        },
        {
          label: $localize`:@@admin.menu.missing-vehicleatstopevents:Felplacerade hållplatser?`,
          address: 'settings/missingVehicleStopEvents',
          isExternal: false,
        },
        {
          label: $localize`:@@admin.menu.customer-display:Hantera kunddisplay`,
          address: 'route/customer-display-admin',
          isExternal: true,
        }
      );
      links.push({
        label: $localize`:@@app.administration:Administration`,
        isExternal: false,
        subMenu: administrationSubMenu,
      });
    }

    this.links = links;
    this.updateActiveStates();
  }

  private updateActiveStates(): void {
    this.subRouteActiveStates = this.links.map(
      (link) =>
        link.subMenu?.some(
          (sub) =>
            sub.address &&
            this.router.isActive(sub.address, {
              paths: 'subset',
              queryParams: 'ignored',
              fragment: 'ignored',
              matrixParams: 'ignored',
            })
        ) ?? false
    );
  }

  init() {
    this.authService.userLoadededEvent$.subscribe((user) => {
      if (!user?.idTokenClaims?.role) {
        return '-';
      }
      const tmp = user.idTokenClaims.role.toString().split(',');
      this.myUserRoles = tmp
        .map((element) => UserRoles[+element])
        .filter((x) => x !== undefined)
        .join(', ');
      this.isSingleRole = this.myUserRoles.split(',').length === 1;
      this.userEmail = user.idTokenClaims.emails ? user.idTokenClaims.emails[0] : '';
      this.showAdministrateMenu = this.authService.userHasAnyRole([
        UserRoles.NobinaLeveransansvarig,
      ]);
      this.showOperatorMenu = this.authService.userHasAnyRole([
        UserRoles.NobinaTrafikledare,
        UserRoles.NobinaLeveransansvarig,
        UserRoles.OperatorTrafikledare,
        UserRoles.OperatorInformator,
      ]);
      this.showManual = this.authService.userHasAnyRole([
        UserRoles.NobinaLeveransansvarig,
        UserRoles.NobinaTrafikledare,
      ]);
      this.manualPath = 'https://share.articulate.com/sfJjxvNOT0wRM8nkCIzoB';
      this.showUEManual = this.authService.userHasAnyRole([UserRoles.UETrafikledare]);
      this.ueManualPath = 'https://trip.nobina.com/manual/introue/index.html#/';
      this.selectedSubcontatorId = user.idTokenClaims.garageIds[0] as string;
      this.userOperatorId = this.authService.userHasAnyRole([UserRoles.OperatorTrafikledare])
        ? (user.idTokenClaims.operatorIds as string[])[0]
        : null;
      if (
        user.idTokenClaims.given_name !== null &&
        user.idTokenClaims.given_name !== undefined &&
        user.idTokenClaims.family_name !== null &&
        user.idTokenClaims.family_name !== undefined
      ) {
        this.displayedUserName =
          user.idTokenClaims.given_name + ' ' + user.idTokenClaims.family_name;
      } else if (user.idTokenClaims.name) {
        this.displayedUserName = user.idTokenClaims.name;
      } else {
        this.displayedUserName = user.idTokenClaims.email as string;
      }

      this.adminService
        .getMyOrganization()
        .pipe(take(1))
        .subscribe((x) => (this.myOrganisation = x.body.organization));

      if (this.authService.userHasAnyRole([UserRoles.NobinaTrafikledare])) {
        this.operatorService
          .getOperators(true)
          .pipe(take(1))
          .subscribe((ops) => {
            if (!ops.body.length) {
              this.mustSetOperators = true;
              this.navigateToSettings();
            }
          });
      }

      this.notificationService.getUnreadNotificationsCount().subscribe((count) => {
        this.numberOfUnreadNotifications = count;
      });

      this._initObservables();
      this.buildLinks();
    });
  }

  navigateToSettings() {
    window.location.href = '/order-new/manage-operators';
  }

  changeLanguage(lang: string) {
    this.setCookie(lang);
    window.location.reload();
  }

  setSelectedLanguage() {
    switch (environment.language) {
      case 'fi':
        this.currentLanguage = 'Suomeksi';
        this.currentFlag = 'assets/img/flags/finish.svg';
        break;
      case 'sv':
        this.currentLanguage = 'Svenska';
        this.currentFlag = 'assets/img/flags/swedish.svg';
        break;
      case 'nb':
        this.currentLanguage = 'Norsk';
        this.currentFlag = 'assets/img/flags/norwegian.svg';
        break;
      case 'da':
        this.currentLanguage = 'Dansk';
        this.currentFlag = 'assets/img/flags/danish.svg';
        break;
    }
  }

  logout() {
    this.authService.logout();
  }

  setCookie(language: string) {
    document.cookie = `language=${language}; path=/; domain=${window.location.hostname}`;
  }
}
